import { Circle, Color, Container, DataItem, Label, Rectangle, color, percent } from "@amcharts/amcharts5";
import { IHierarchyDataItem, IPackDataItem, ITreemapDataItem, Pack, Treemap } from "@amcharts/amcharts5/hierarchy";
import { cloneDeep, map } from "lodash";

import BaseChart, { LegendData } from "@/classes/BaseChart";
import { PLAN_MODULE } from "@/constants";
import Lang from "@/lang";
import { ChartDataRecord, MapDataItem, TooltipSeries } from "@/types/charts";

const LIGHT_STROKE = {
  strokeWidth: 1,
  strokeOpacity: 0.2,
};

const NO_STROKE = {
  strokeWidth: 0,
  strokeOpacity: 0,
};

const TOP_DATA_LABEL_KEY = "__isTopDataLabel";

export default class Hierarchy extends BaseChart {
  isTreeMap!: boolean;
  private dataLabelsTopLimit?: number;
  private dataLabelsTotal = 0;

  initChart() {
    this.isTreeMap = this.chartName === "treeMap";

    this.root.container.children.clear();

    this.chart = this.root.container.children.push(
      Container.new(this.root, {
        width: percent(100),
        height: percent(100),
        layout: this.layout,
      })
    );

    const { legendData } = this.createSeries();
    this.showLegend(map(this.data, "categoryField") as string[], legendData as LegendData, false);
  }

  protected createSeries() {
    const initialSeriesConfig = {
      singleBranchOnly: false,
      downDepth: 1,
      valueField: "valueField",
      categoryField: "categoryField",
      childDataField: "children",
      initialDepth: 1,
      topLevel: 1,
    };

    const series: Treemap & Pack = this.chart.children.push(
      this.isTreeMap
        ? Treemap.new(this.root, {
            ...initialSeriesConfig,
            nodePaddingOuter: 0,
            nodePaddingInner: 0,
          })
        : Pack.new(this.root, initialSeriesConfig)
    );

    this.createTooltip(series as TooltipSeries, (target) => {
      let param: Record<string, unknown>[] = [];
      const tooltipValues = target?.dataItem?.dataContext as Record<string, unknown>;

      if (tooltipValues) {
        param = [
          { key: this.settings.decompositionMetric, value: tooltipValues.categoryField },
          { key: this.settings.displayMetric, value: tooltipValues.valueField },
        ];
      }
      return param;
    });

    if (this.isTreeMap) {
      series.rectangles.template.setAll(NO_STROKE);
    } else {
      series.circles.template.setAll(LIGHT_STROKE);
    }

    const data = {
      name: "Root",
      children: this.data,
    };

    series.labels.template.adapters.add("fill", (fill, target: Label) => {
      if (target.dataItem) {
        const bgcolor = this.isTreeMap
          ? (target.dataItem as DataItem<ITreemapDataItem>)?.get("rectangle").get("fill")
          : (target.dataItem as DataItem<IPackDataItem>)?.get("circle").get("fill");

        if (bgcolor) {
          return Color.alternative(bgcolor, color(0x000000), color(0xffffff));
        }
      }
      return fill;
    });

    if (this.settings.showDataLabels === true) {
      const metricFormatter = this.formatters?.[this.settings.displayMetric];
      const formatterConfig = this.getDataLabelsFormatterConfig();

      series.labels.template.adapters.add("text", (text, target) => {
        const dataItem = target.dataItem as DataItem<IPackDataItem> | DataItem<ITreemapDataItem> | undefined;
        if (!dataItem) {
          return text ?? "";
        }

        const context = dataItem.dataContext as Record<string, unknown> | undefined;
        const baseText = text ?? "";
        const baseCategory = baseText.split("\n")[0] ?? "";

        const category = (context?.categoryField as string) ?? baseCategory;

        const rawValue = context?.valueField;
        if (typeof rawValue !== "number") {
          return category;
        }

        if (this.dataLabelsTopLimit && context?.[TOP_DATA_LABEL_KEY] !== true) {
          return category;
        }

        const formattedValue = metricFormatter
          ? metricFormatter(rawValue, formatterConfig)
          : this.root.numberFormatter.format(rawValue, formatterConfig as any);

        if (!formattedValue) {
          return category;
        }

        const percentText = this.getPercentSuffix(rawValue);
        if (!percentText) {
          return `${category}\n${formattedValue}`;
        }

        return `${category}\n${formattedValue} (${percentText})`;
      });
    }

    if (!this.isTreeMap) {
      series.circles.template.set("forceInactive", true);

      series.circles.template.adapters.add("forceInactive", (forceInactive, target: Circle) => {
        const key: keyof IHierarchyDataItem = "depth";
        const dataItem = target?.dataItem as DataItem<IHierarchyDataItem>;
        if (dataItem) {
          return dataItem.get(key) == 1 ? false : forceInactive;
        }
      });
    } else {
      series.rectangles.template.adapters.add("forceHidden", (forceHidden, target: Rectangle) => {
        const key: keyof IHierarchyDataItem = "depth";
        const dataItem = target?.dataItem as DataItem<IHierarchyDataItem>;
        if (dataItem) {
          return dataItem.get(key) !== 1;
        }
      });
    }
    series.get("colors")?.set("colors", this.palette);
    series.data.setAll([data]);

    const rootLevel = series.dataItems[0];
    const children = rootLevel.get("children");

    if (children?.length) {
      series.set("selectedDataItem", rootLevel);

      children.forEach((item: DataItem<IHierarchyDataItem>, index: number) => {
        item.set("fill", this.getSeriesColor(index));
      });
    }

    return { legendData: children };
  }

  protected mapData(data: ChartDataRecord[]): ChartDataRecord[] {
    this.dataLabelsTotal = 0;
    this.dataLabelsTopLimit = this.getDataLabelsTopLimit();
    let groupedData: ChartDataRecord = {};

    if (!data || !Array.isArray(data)) {
      return [];
    }

    let preparedData: ChartDataRecord[] = cloneDeep(data);

    if (this.module === PLAN_MODULE && this.settings?.showItems && Number.isFinite(this.settings?.showItems)) {
      const decompositionMetric = this.settings.decompositionMetric;
      const displayMetric = this.settings.displayMetric;

      preparedData = this.prepareTopPlanModuleData(
        preparedData,
        decompositionMetric,
        displayMetric,
        this.settings.showItems
      );
    }

    groupedData = preparedData.reduce((acc: ChartDataRecord, curr: ChartDataRecord) => {
      return {
        ...acc,
        [(curr[this.settings.decompositionMetric] || Lang.null) as string]:
          ((acc[this.settings.decompositionMetric] as number) || 0) + (curr[this.settings.displayMetric] as number) ||
          0,
      };
    }, {});

    const resultData = Object.keys(groupedData)
      .map((key: string) => ({
        categoryField: key,
        valueField: groupedData[key],
      }))
      .sort((a, b) => (b.valueField as number) - (a.valueField as number));

    if (this.dataLabelsTopLimit) {
      resultData.forEach((item, index) => {
        (item as Record<string, unknown>)[TOP_DATA_LABEL_KEY] = index < (this.dataLabelsTopLimit as number);
      });
    }

    const result = this.showItems(resultData, "categoryField", "valueField");

    this.dataLabelsTotal = result.reduce((acc, item) => {
      const raw = (item as MapDataItem).valueField;
      const numeric = typeof raw === "number" ? raw : Number(raw);
      return Number.isFinite(numeric) ? acc + numeric : acc;
    }, 0);

    return result.filter((item: ChartDataRecord) => (item.valueField as number) > 0) as MapDataItem[];
  }

  private shouldShowPercentLabels(): boolean {
    return Boolean(this.settings?.dataLabelsShowPercent);
  }

  private getPercentSuffix(value: number): string | undefined {
    if (!this.shouldShowPercentLabels() || !Number.isFinite(value) || this.dataLabelsTotal <= 0) {
      return undefined;
    }

    const share = value / this.dataLabelsTotal;
    if (!Number.isFinite(share)) {
      return undefined;
    }

    const decimals = this.getDataLabelsDecimals();
    const precision = decimals !== undefined ? decimals : 1;
    const clampedPrecision = Math.min(Math.max(precision, 0), 6);
    const percentValue = share * 100;

    return `${percentValue.toFixed(clampedPrecision)}%`;
  }
}
